/**
 * HierarchyBadge.js
 *
 * A compact visual badge that communicates the current hierarchy level
 * (Enterprise / Department / Discipline) with colour-coded styling.
 *
 * Props:
 *   level   - 'enterprise' | 'department' | 'discipline'
 *   size    - 'sm' | 'md' (default 'sm')
 *   showIcon - boolean (default true)
 */

import React from 'react';
import { FiGlobe, FiGrid, FiTarget } from 'react-icons/fi';
import { LEVEL_META } from '../context/HierarchyContext';

const LEVEL_STYLES = {
  enterprise: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    border: 'border-indigo-300',
    icon: <FiGlobe className="inline-block" />,
  },
  department: {
    bg: 'bg-sky-100',
    text: 'text-sky-800',
    border: 'border-sky-300',
    icon: <FiGrid className="inline-block" />,
  },
  discipline: {
    bg: 'bg-teal-100',
    text: 'text-teal-800',
    border: 'border-teal-300',
    icon: <FiTarget className="inline-block" />,
  },
};

const SIZE_STYLES = {
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-sm px-3 py-1 gap-1.5',
};

const HierarchyBadge = ({ level = 'enterprise', size = 'sm', showIcon = true, className = '' }) => {
  const styles = LEVEL_STYLES[level] || LEVEL_STYLES.enterprise;
  const meta = LEVEL_META[level] || LEVEL_META.enterprise;
  const sizeStyle = SIZE_STYLES[size] || SIZE_STYLES.sm;

  return (
    <span
      title={meta.description}
      className={`inline-flex items-center font-medium rounded-full border ${styles.bg} ${styles.text} ${styles.border} ${sizeStyle} ${className}`}
    >
      {showIcon && (
        <span className="flex-shrink-0">
          {styles.icon}
        </span>
      )}
      {meta.label}
    </span>
  );
};

export default HierarchyBadge;
